/*
 *  ___                  __                __  _ _
 *   | |_  _     _ |_ |_|_    _  _ _ _ _  /  \(_(_|. _  _
 *   | | )(_)|_|(_)| )|_|__)(|_)| (-_)_)  \__/| | ||| )(-
 *              _/           |
 */

import { hasValue, verify, validEmail } from '../../../../src/core/validations'
import dayjs from 'dayjs'

import {
  validNHIS,
  validVotersID,
  validSSNIT,
  validPassport,
  validDrivers,
  validID,
  validName,
  validCellnumber,
  onlyDigits,
} from '../../validations'

export const validate = (state, requiredOnly, validateOnly) => {
  if (process.env.DISABLE_VALIDATIONS === 'true') {
    return true
  }

  let validates = true
  switch (state.step) {
    case 'quotation-screen':
      validates =
        verify(
          state,
          state.form['life-assured-identification']['date-of-birth'],
          hasValue,
          !validateOnly && { id: 'date-of-birth' },
          ['*']
        ) && validates

      validates =
        verify(
          state,
          state.form['life-assured-identification']['date-of-birth'],
          (v) =>
            hasValue(v) &&
            (requiredOnly ||
              (dayjs().diff(v, 'year') >= 18 && dayjs().diff(v, 'year') <= 74)),
          !validateOnly && { id: 'date-of-birth' },
          requiredOnly
            ? ['*']
            : ['You must be 18-74 years of age to apply for this product.']
        ) && validates

      validates =
        verify(
          state,
          state.form['life-assured-identification']['gender'],
          hasValue,
          !validateOnly && { id: 'gender' },
          ['*']
        ) && validates

      validates =
        verify(
          state,
          state.form['cover-options']['gmd5000'] ||
            state.form['cover-options']['gmd10000'] ||
            state.form['cover-options']['gmd15000'] ||
            state.form['cover-options']['gmd20000'] ||
            state.form['cover-options']['gmd30000'] ||
            state.form['cover-options']['gmd40000'] ||
            state.form['cover-options']['gmd50000'],
          hasValue,
          !validateOnly && { id: 'product-plan-type-radio-group' },
          ['*']
        ) && validates

      validates =
        verify(
          state,
          state.form['one-year-memorial'],
          (v) => v === null || v === '' || v === 'Y',
          !validateOnly && { id: 'one-year-memorial' },
          ['*']
        ) && validates

      validates =
        verify(
          state,
          state.form['cash-bonus'],
          (v) => {
            if (v === null || v === '' || isNaN(Number(v))) return true
            const value = Number(v)
            if (value < 70) return false
            // Get cover and lives index
            const coverOptions = state.form['cover-options']
            const livesAssured = state.form['lives-assured']
            let coverOption = 0
            if (coverOptions['gmd5000'] === 'Y') coverOption = 1
            else if (coverOptions['gmd10000'] === 'Y') coverOption = 2
            else if (coverOptions['gmd15000'] === 'Y') coverOption = 3
            else if (coverOptions['gmd20000'] === 'Y') coverOption = 4
            else if (coverOptions['gmd30000'] === 'Y') coverOption = 5
            else if (coverOptions['gmd40000'] === 'Y') coverOption = 6
            else if (coverOptions['gmd50000'] === 'Y') coverOption = 7
            if (!coverOption) return false
            const coverIdx = coverOption - 1
            const livesIdx = Math.min((livesAssured || []).length, 14)
            const max = [
              [162.15, 165.21, 168.39, 171.59, 177.85, 184.28, 190.74],
              [191.19, 223.22, 255.46, 287.65, 352.0, 416.41, 481.05],
              [220.22, 281.24, 342.53, 403.72, 526.15, 648.54, 771.37],
              [249.25, 339.25, 429.61, 519.78, 700.3, 880.67, 1061.68],
              [278.29, 397.27, 516.68, 635.85, 874.45, 1112.8, 1352.0],
              [307.32, 455.29, 603.76, 751.91, 1048.6, 1344.92, 1642.31],
              [336.36, 513.3, 690.83, 867.98, 1222.75, 1577.05, 1932.63],
              [365.39, 571.32, 777.91, 984.04, 1396.9, 1809.18, 2222.95],
              [394.42, 629.33, 864.98, 1100.11, 1571.05, 2041.31, 2513.26],
              [423.46, 687.35, 952.06, 1216.17, 1745.2, 2273.44, 2803.58],
              [452.49, 745.36, 1039.13, 1332.23, 1919.35, 2505.57, 3093.89],
              [481.53, 803.38, 1126.21, 1448.3, 2093.5, 2737.7, 3384.21],
              [510.56, 861.39, 1213.28, 1564.36, 2267.65, 2969.83, 3674.53],
              [539.6, 919.41, 1300.36, 1680.43, 2441.8, 3201.96, 3964.84],
              [568.63, 977.43, 1387.43, 1796.49, 2615.95, 3434.09, 4255.16],
            ][livesIdx][coverIdx]
            return value <= max
          },
          !validateOnly && { id: 'cash-bonus' },
          [
            'Cash bonus must be at least GMD 70 and not more than the risk cover amount.',
          ]
        ) && validates

      validates =
        verify(
          state,
          state.form['inflation-protector'],
          (v) => ['0', '5', '10', '15', '20', '25', '30'].includes(v),
          !validateOnly && { id: 'inflation-protector' },
          ['*']
        ) && validates
      break

    case 'family-members-age-and-gender':
      validates =
        verify(
          state,
          state.local && state.local['age-of-member'],
          hasValue,
          !validateOnly && { id: 'age-of-member' },
          ['*']
        ) && validates

      validates =
        verify(
          state,
          state.local && state.local.gender,
          hasValue,
          !validateOnly && { id: 'gender' },
          ['*']
        ) && validates

      //Validate the gender for main life and spouse
      if (['Spouse'].indexOf(state.local.relationship) >= 0) {
        if (state.form['life-assured-identification']['gender'] === 'Female') {
          validates =
            verify(
              state,
              state.local.gender,
              (v) =>
                hasValue(v) &&
                (requiredOnly || ['Male'].indexOf(state.local.gender) >= 0),
              !validateOnly && { id: 'gender' },
              requiredOnly
                ? ['*']
                : [
                    'The main life gender must be opposite to the spouse gender selected.',
                  ]
            ) && validates
        }
      }

      //Validate the gender for main life and spouse
      if (
        ['Spouse', 'Additional Spouse'].indexOf(state.local.relationship) >= 0
      ) {
        if (state.form['life-assured-identification']['gender'] === 'Male') {
          validates =
            verify(
              state,
              state.local.gender,
              (v) =>
                hasValue(v) &&
                (requiredOnly || ['Female'].indexOf(state.local.gender) >= 0),
              !validateOnly && { id: 'gender' },
              requiredOnly
                ? ['*']
                : [
                    'The main life gender must be opposite to the spouse gender selected.',
                  ]
            ) && validates
        }
      }

      //Validate the gender & relationship
      if (
        [
          'Mother',
          'Mother in law',
          'Grandmother',
          'Step mother',
          'Sister',
        ].indexOf(state.local.relationship) >= 0
      ) {
        validates =
          verify(
            state,
            state.local.gender,
            (v) =>
              hasValue(v) &&
              (requiredOnly || ['Female'].indexOf(state.local.gender) >= 0),
            !validateOnly && { id: 'gender' },
            requiredOnly
              ? ['*']
              : ['The selected relationship and gender does not correspond.']
          ) && validates
      }

      if (
        [
          'Father',
          'Father in law',
          'Grandfather',
          'Step father',
          'Brother',
        ].indexOf(state.local.relationship) >= 0
      ) {
        validates =
          verify(
            state,
            state.local.gender,
            (v) =>
              hasValue(v) &&
              (requiredOnly || ['Male'].indexOf(state.local.gender) >= 0),
            !validateOnly && { id: 'gender' },
            requiredOnly
              ? ['*']
              : ['The selected relationship and gender does not correspond.']
          ) && validates
      }

      if (['Child'].indexOf(state.local.relationship) >= 0) {
        validates =
          verify(
            state,
            state.local.gender,
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                ['Male', 'Female'].indexOf(state.local.gender) >= 0),
            !validateOnly && { id: 'gender' },
            requiredOnly
              ? ['*']
              : ['The selected relationship and gender does not correspond.']
          ) && validates
      }
      break

    case 'personal-details':
      validates =
        verify(
          state,
          state.form['life-assured-identification']['first-names'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'first-names' },
          requiredOnly ? ['*'] : ['Please enter a valid first-name']
        ) && validates
      validates =
        verify(
          state,
          state.form['life-assured-identification'].surname,
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'surname' },
          requiredOnly ? ['*'] : ['Please enter a valid surname']
        ) && validates
      validates =
        verify(
          state,
          state.form['life-assured-identification']['idpassport-number'],
          hasValue,
          !validateOnly && { id: 'idpassport-number' },
          ['*']
        ) && validates
      validates =
        verify(
          state,
          state.form['life-assured-identification']['id-type-radio-group'],
          (group) => Object.values(group || {}).some((v) => !!v),
          !validateOnly && { id: 'id-type-radio-group' },
          ['*']
        ) && validates
      validates =
        verify(
          state,
          state.form['life-assured-identification'].nationality,
          hasValue,
          !validateOnly && { id: 'nationality' },
          ['*']
        ) && validates
      validates =
        verify(
          state,
          state.form['life-assured-identification'].occupation,
          hasValue,
          !validateOnly && { id: 'occupation' },
          ['*']
        ) && validates
      validates =
        verify(
          state,
          state.form['life-assured-contact-details']['cell-phone'],
          hasValue,
          !validateOnly && { id: 'cell-phone' },
          ['*']
        ) && validates
      validates =
        verify(
          state,
          state.form['life-assured-contact-details'].email,
          validEmail,
          !validateOnly && { id: 'email' },
          ['*']
        ) && validates
      validates =
        verify(
          state,
          state.form['life-assured-postal-address'].town,
          hasValue,
          !validateOnly && { id: 'town' },
          ['*']
        ) && validates
      validates =
        verify(
          state,
          state.form['life-assured-postal-address'].region,
          hasValue,
          !validateOnly && { id: 'region' },
          ['*']
        ) && validates
      if (state.form['life-assured-identification']['gender'] === 'Female') {
        validates =
          verify(
            state,
            state.form['life-assured-identification']['title'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                ['Miss', 'Ms', 'Mrs', 'Adv', 'Dr', 'Prof', 'Rev'].indexOf(
                  state.form['life-assured-identification']['title']
                ) >= 0),
            !validateOnly && { id: 'title' },
            requiredOnly
              ? ['*']
              : ['The selected title and gender does not correspond.']
          ) && validates
      }

      if (state.form['life-assured-identification']['gender'] === 'Male') {
        validates =
          verify(
            state,
            state.form['life-assured-identification']['title'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                ['Mr', 'Adv', 'Dr', 'Prof', 'Rev'].indexOf(
                  state.form['life-assured-identification']['title']
                ) >= 0),
            !validateOnly && { id: 'title' },
            requiredOnly
              ? ['*']
              : ['The selected title and gender does not correspond.']
          ) && validates
      }

      // Gender consistency with quote selection
      validates =
        verify(
          state,
          state.form['life-assured-identification'].gender,
          (gender) => {
            if (!gender) return true // Only check if gender is set
            return gender === state.form['initial-gender']
          },
          !validateOnly && { id: 'gender' },
          ['Gender must match what was selected on the previous screen.']
        ) && validates

      validates =
        verify(
          state,
          state.form['life-assured-contact-details']['cell-phone'],
          (v) => (requiredOnly ? hasValue(v) : validCellnumber(v)),
          !validateOnly && { id: 'cell-phone' },
          requiredOnly ? ['*'] : ['Please enter a valid Cellphone Number']
        ) && validates

      validates =
        verify(
          state,
          state.form['life-assured-contact-details'].email,
          (v) => (requiredOnly ? hasValue(v) : validEmail(v)),
          !validateOnly && { id: 'email' },
          requiredOnly ? ['*'] : ['Please enter a valid Email address']
        ) && validates

      // Marital status must be 'Married' if a spouse is present in lives-assured
      const hasSpouse = (state.form['lives-assured'] || []).some(
        (life) => life.relationship === 'Spouse'
      )
      if (hasSpouse) {
        validates =
          verify(
            state,
            state.form['life-assured-identification']['marital-status'],
            (v) => v === 'Married',
            !validateOnly && { id: 'marital-status' },
            [
              'You cannot add a spouse unless you are married. Please set marital status to "Married" or remove the spouse.',
            ]
          ) && validates
      }
      break

    case 'trustee-details':
      validates =
        verify(
          state,
          state.form['trustee-identification']['first-names'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'first-names' },
          requiredOnly ? ['*'] : ['Please enter a valid first-name']
        ) && validates
      validates =
        verify(
          state,
          state.form['trustee-identification'].surname,
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'surname' },
          requiredOnly ? ['*'] : ['Please enter a valid surname']
        ) && validates
      // Validate age
      validates =
        verify(
          state,
          state.form['trustee-identification']['date-of-birth'],
          (v) => hasValue(v) && (requiredOnly || dayjs().diff(v, 'year') >= 18),
          !validateOnly && { id: 'date-of-birth' },
          requiredOnly
            ? ['*']
            : ['The minimum allowable age for this product is 18.']
        ) && validates
      //Validate the gender & title
      if (state.form['trustee-identification'].gender === 'Female') {
        validates =
          verify(
            state,
            state.form['trustee-identification'].title,
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                ['Miss', 'Ms', 'Mrs', 'Adv', 'Dr', 'Prof', 'Rev'].indexOf(
                  state.form['trustee-identification'].title
                ) >= 0),
            !validateOnly && { id: 'title' },
            requiredOnly
              ? ['*']
              : ['The selected title and gender does not correspond.']
          ) && validates
      }

      if (state.form['trustee-identification'].gender === 'Male') {
        validates =
          verify(
            state,
            state.form['trustee-identification'].title,
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                ['Mr', 'Adv', 'Dr', 'Prof', 'Rev'].indexOf(
                  state.form['trustee-identification'].title
                ) >= 0),
            !validateOnly && { id: 'title' },
            requiredOnly
              ? ['*']
              : ['The selected title and gender does not correspond.']
          ) && validates
      }

      //Validate the gender & relationship
      if (state.form['trustee-identification']['gender'] === 'Female') {
        validates =
          verify(
            state,
            state.form['trustee-identification']['relationship'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                [
                  'Daughter-1',
                  'Daughter-2',
                  'Daughter-3',
                  'Daughter-4',
                  'Daughter-5',
                  'Grandmother',
                  'Mother',
                  'Mother-In-Law',
                  'Sister',
                  'Spouse',
                  'Spouse-2',
                  'Spouse-3',
                  'Spouse-4',
                  'Spouse-5',
                  'Testament',
                  'Trust',
                ].indexOf(
                  state.form['trustee-identification']['relationship']
                ) >= 0),
            !validateOnly && { id: 'relationship' },
            requiredOnly
              ? ['*']
              : ['The selected relationship and gender does not correspond.']
          ) && validates
      }

      if (state.form['trustee-identification']['gender'] === 'Male') {
        validates =
          verify(
            state,
            state.form['trustee-identification']['relationship'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                [
                  'Brother',
                  'Estate',
                  'Father',
                  'Son-1',
                  'Son-2',
                  'Son-3',
                  'Son-4',
                  'Son-5',
                  'Father-In-Law',
                  'Grandfather',
                  'Spouse',
                  'Spouse-2',
                  'Spouse-3',
                  'Spouse-4',
                  'Spouse-5',
                  'Testament',
                  'Trust',
                ].indexOf(
                  state.form['trustee-identification']['relationship']
                ) >= 0),
            !validateOnly && { id: 'relationship' },
            requiredOnly
              ? ['*']
              : ['The selected relationship and gender does not correspond.']
          ) && validates
      }
      validates =
        verify(
          state,
          state.form['trustee-identification']['cell-phone'],
          (v) => (requiredOnly ? hasValue(v) : validCellnumber(v)),
          !validateOnly && { id: 'cell-phone' },
          requiredOnly ? ['*'] : ['Please enter a valid Cellphone Number']
        ) && validates

      //Validate the gender & relationship
      if (
        ['Adv', 'Dr', 'Prof', 'Rev'].indexOf(
          state.form['trustee-identification']['title']
        ) >= 0
      ) {
        validates =
          verify(
            state,
            state.form['trustee-identification']['relationship'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                [
                  'Daughter-1',
                  'Daughter-2',
                  'Daughter-3',
                  'Daughter-4',
                  'Daughter-5',
                  'Estate',
                  'Grandmother',
                  'Mother',
                  'Mother-In-Law',
                  'Sister',
                  'Spouse',
                  'Spouse-2',
                  'Spouse-3',
                  'Spouse-4',
                  'Spouse-5',
                  'Testament',
                  'Trust',
                  'Brother',
                  'Father',
                  'Son-1',
                  'Son-2',
                  'Son-3',
                  'Son-4',
                  'Son-5',
                  'Father-In-Law',
                  'Grandfather',
                ].indexOf(
                  state.form['trustee-identification']['relationship']
                ) >= 0),
            !validateOnly && { id: 'relationship' },
            requiredOnly
              ? ['*']
              : ['The selected relationship and title does not correspond.']
          ) && validates
      }
      if (
        ['Miss', 'Mrs', 'Ms'].indexOf(
          state.form['trustee-identification']['title']
        ) >= 0
      ) {
        validates =
          verify(
            state,
            state.form['trustee-identification']['relationship'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                [
                  'Daughter-1',
                  'Daughter-2',
                  'Daughter-3',
                  'Daughter-4',
                  'Daughter-5',
                  'Estate',
                  'Grandmother',
                  'Mother',
                  'Mother-In-Law',
                  'Sister',
                  'Spouse',
                  'Spouse-2',
                  'Spouse-3',
                  'Spouse-4',
                  'Spouse-5',
                  'Testament',
                  'Trust',
                ].indexOf(
                  state.form['trustee-identification']['relationship']
                ) >= 0),
            !validateOnly && { id: 'relationship' },
            requiredOnly
              ? ['*']
              : ['The selected relationship and title does not correspond.']
          ) && validates
      }
      if (state.form['trustee-identification']['title'] === 'Mr') {
        validates =
          verify(
            state,
            state.form['trustee-identification']['relationship'],
            (v) =>
              hasValue(v) &&
              (requiredOnly ||
                [
                  'Brother',
                  'Estate',
                  'Father',
                  'Son-1',
                  'Son-2',
                  'Son-3',
                  'Son-4',
                  'Son-5',
                  'Father-In-Law',
                  'Grandfather',
                  'Spouse',
                  'Spouse-2',
                  'Spouse-3',
                  'Spouse-4',
                  'Spouse-5',
                  'Testament',
                  'Trust',
                ].indexOf(
                  state.form['trustee-identification']['relationship']
                ) >= 0),
            !validateOnly && { id: 'relationship' },
            requiredOnly
              ? ['*']
              : ['The selected relationship and title does not correspond.']
          ) && validates
      }
      break

    case 'additional-lives':
      validates = state.form['lives-assured'].reduce((valid, life, counter) => {
        valid = ['first-names', 'surname', 'date-of-birth'].reduce(
          (valid, field) => valid && hasValue(life[field]),
          valid
        )
        if (!requiredOnly && valid) {
          valid =
            verify(
              state,
              life['date-of-birth'],
              (v) =>
                dayjs().diff(v, 'year').toString() === life['age-of-member'],
              !validateOnly && { id: `date-of-birth-${counter}` },
              [
                `Date of birth does not match indicated age of ${life['age-of-member']} for ${life['relationship']}`,
              ]
            ) && valid
        }
        if (!requiredOnly && valid) {
          valid =
            verify(
              state,
              life['first-names'],
              (v) => (requiredOnly ? hasValue(v) : validName(v)),
              !validateOnly && { id: `first-names-${counter}` },
              requiredOnly ? ['*'] : ['Please enter a valid first-name']
            ) && valid
        }
        if (!requiredOnly && valid) {
          valid =
            verify(
              state,
              life['surname'],
              (v) => (requiredOnly ? hasValue(v) : validName(v)),
              !validateOnly && { id: `surname-${counter}` },
              requiredOnly ? ['*'] : ['Please enter a valid surname']
            ) && valid
        }
        return valid
      }, validates)
      break

    case 'payment-details':
      validates =
        verify(
          state,
          state.form['payment-frequency'] &&
            (state.form['payment-frequency'].monthly === 'Y' ||
              state.form['payment-frequency'].quarterly === 'Y' ||
              state.form['payment-frequency'].yearly === 'Y'),
          hasValue,
          !validateOnly && { id: 'payment-frequency-radio-group' },
          ['*']
        ) && validates
      validates =
        verify(
          state,
          state.form['payment-start-date'],
          hasValue,
          !validateOnly && { id: 'payment-start-date' },
          ['*']
        ) && validates
      validates =
        verify(
          state,
          state.form['debit-order'] === 'Y' || state.form['stop-order'] === 'Y',
          hasValue,
          !validateOnly && { id: 'payment-method-radio-group' },
          ['*']
        ) && validates
      break

    case 'debit-order-details':
      validates = Object.keys(state.form['payment-bank-details']).reduce(
        (valid, field) =>
          verify(
            state,
            state.form['payment-bank-details'][field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && valid,
        validates
      )

      validates =
        verify(
          state,
          state.form['payer-signature'],
          hasValue,
          !validateOnly && { id: 'interactive-canvas-html' },
          requiredOnly ? ['*'] : ['Your signature is required']
        ) && validates

      //Validate field name to have no numbers
      validates =
        verify(
          state,
          state.form['payment-bank-details']['account-holder-name'],
          (v) => (requiredOnly ? hasValue(v) : validName(v)),
          !validateOnly && { id: 'account-holder-name' },
          requiredOnly ? ['*'] : ['Please remove all numbers in the field']
        ) && validates

      //VALIDATE ACCOUNT NUMBER LENGTH BASED ON THE ACCOUNT ENTITY NAME CHOSEN
      if (!requiredOnly) {
        //10 DIGITS //11 DIGITS //12 DIGITS //13DIGITS// 14 DIGITS// 15 DIGITS// 16 DIGITS
        if (
          (state.form['payment-bank-details']['account-entity-name'] ===
            'Bsic Gambia' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Ecobank Gambia') &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) =>
                (v.length === 10 ||
                  v.length === 11 ||
                  v.length === 12 ||
                  v.length === 13 ||
                  v.length === 14 ||
                  v.length === 15 ||
                  v.length === 16) &&
                onlyDigits(v),
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //12 / 13 / 14 / 15 DIGITS
        else if (
          (state.form['payment-bank-details']['account-entity-name'] ===
            'Vista bank' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Megabank Gambia Limited') &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) =>
                (v.length === 12 ||
                  v.length === 13 ||
                  v.length === 14 ||
                  v.length === 15) &&
                onlyDigits(v),
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //13 DIGITS
        else if (
          (state.form['payment-bank-details']['account-entity-name'] ===
            'Access Bank Gambia' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Standard Chartered Bank' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Bloom Bank Africa') &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) => v.length === 13 && onlyDigits(v),
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //11 DIGITS
        else if (
          (state.form['payment-bank-details']['account-entity-name'] ===
            'Trust Bank Gambia' ||
            state.form['payment-bank-details']['account-entity-name'] ===
              'Supersonicz') &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) => v.length === 11 && onlyDigits(v),
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //15 DIGITS
        else if (
          state.form['payment-bank-details']['account-entity-name'] ===
            'Fbn bank' &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) => v.length === 15 && onlyDigits(v),
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //18 DIGITS
        else if (
          state.form['payment-bank-details']['account-entity-name'] ===
            'Arab Gambia Islamic Bank' &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) => v.length === 18 && onlyDigits(v),
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //10 DIGITS
        else if (
          state.form['payment-bank-details']['account-entity-name'] ===
            'Zenith Bank Gambia' &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) => v.length === 10 && onlyDigits(v),
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //13 /14 /15 /16 /17 DIGITS
        else if (
          state.form['payment-bank-details']['account-entity-name'] ===
            'Reliance Gambia' &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) =>
                (v.length === 13 ||
                  v.length === 14 ||
                  v.length === 15 ||
                  v.length === 16 ||
                  v.length === 17) &&
                onlyDigits(v),
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
        //10 /11 /12 /13 DIGITS
        else if (
          state.form['payment-bank-details']['account-entity-name'] ===
            'Gt Bank Gambia' &&
          state.form['payment-bank-details']['account-number'] !== null
        ) {
          validates =
            verify(
              state,
              state.form['payment-bank-details']['account-number'],
              (v) =>
                (v.length === 10 ||
                  v.length === 11 ||
                  v.length === 12 ||
                  v.length === 13) &&
                onlyDigits(v),
              !validateOnly && { id: 'account-number' },
              ['Please enter a valid account number']
            ) && validates
        }
      }
      break

    case 'stop-order-details':
      validates = Object.keys(state.form['payment-employment-details']).reduce(
        (valid, field) =>
          verify(
            state,
            state.form['payment-employment-details'][field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && valid,
        validates
      )
      validates =
        verify(
          state,
          state.form['payer-signature'],
          hasValue,
          !validateOnly && { id: 'interactive-canvas-html' },
          requiredOnly ? ['*'] : ['Your signature is required']
        ) && validates
      break

    case 'mobile-wallet-details':
      validates = Object.keys(state.form['mobile-wallet-details']).reduce(
        (valid, field) =>
          verify(
            state,
            state.form['mobile-wallet-details'][field],
            hasValue,
            !validateOnly && { id: field },
            ['*']
          ) && valid,
        validates
      )
      break

    case 'upload-documents':
      const docs = state.form.documents || {}
      const hasAnyDoc = Object.values(docs).some(
        (arr) => Array.isArray(arr) && arr.some((doc) => doc)
      )
      validates =
        verify(
          state,
          hasAnyDoc,
          (v) => v === true,
          !validateOnly && { id: 'documents-list', type: 'list' },
          ['You must upload an identification document before proceeding.']
        ) && validates
      break

    case 'acceptance-screen':
      validates =
        verify(
          state,
          state.form['acceptance-signature'],
          hasValue,
          !validateOnly && { id: 'interactive-canvas-html' },
          requiredOnly ? ['*'] : ['Your signature is required']
        ) && validates
      break
  }
  if (!requiredOnly && !validates) {
    state.local = state.local || {}
    state.local.failed_validation = true
  }
  return validates
}
